package com.downdogapp.client

import com.downdogapp.*
import com.downdogapp.Cursor.DEFAULT
import com.downdogapp.Display.NONE
import com.downdogapp.Overflow.HIDDEN
import com.downdogapp.Overflow.SCROLL
import com.downdogapp.Position.ABSOLUTE
import com.downdogapp.Position.RELATIVE
import com.downdogapp.Size.INHERIT
import com.downdogapp.ZIndex.Companion
import com.downdogapp.client.api.AppType
import com.downdogapp.client.api.GiftProduct
import com.downdogapp.client.api.SignInReason
import com.downdogapp.client.api.SignInReason.*
import com.downdogapp.client.controllers.*
import com.downdogapp.client.controllers.playback.PlaybackViewController
import com.downdogapp.client.controllers.start.MenuPage
import com.downdogapp.client.controllers.start.StartViewController
import com.downdogapp.client.singleton.*
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.events.KeyboardEvent

object AppPage {

  private val viewControllers: List<ViewController> = mutableListOf()
  private lateinit var viewControllerFrame: HTMLDivElement
  private lateinit var spinner: HTMLDivElement

  var DEBUG: Boolean = false; private set

  val displayingAboveStartView
    get() =
      App.getViewController(StartViewController::class) != null

  val displayingAbovePlayback
    get() =
      App.getViewController(PlaybackViewController::class) != null

  fun initialize(viewController: Lazy<ViewController>,
                 signInReason: SignInReason?,
                 titleLabel: String? = null,
                 subtitleLabel: String? = null,
                 debug: Boolean = false,
                 redirectOnSignIn: String? = null,
                 requiredEmailDomain: String? = null,
                 onFinish: () -> Unit = {}) {
    DEBUG = debug
    App.incrementBackgroundImage()

    initializeDocument()

    App.fetchManifest(signInReason = signInReason) {
      spinnerShowing = false
      if (MANIFEST.displayLoginOnStart) {
        App.pushViewController(
          if (signInReason == null && redirectOnSignIn == null)
            WelcomeViewController { window.location.reload() }
          else
            SignInViewController(
              isSignUp = when (signInReason) {
                PURCHASE -> false
                REFERRAL -> true
                GIFT_PURCHASE -> false
                GIFT_REDEMPTION -> true
                CHALLENGE_ENROLLMENT -> false
                SUPPORT -> false
                PLAYBACK -> false
                null -> true
              },
              titleLabel = titleLabel ?: MANIFEST.signUpTitle,
              subtitleLabel = subtitleLabel ?: MANIFEST.signUpSubtitle,
              isReferral = signInReason == REFERRAL,
              requiredEmailDomain = requiredEmailDomain
            ) {
              redirectOnSignIn?.let {
                goTo(it)
              } ?: window.location.reload()
            }
        )
      } else {
        App.pushViewController(viewController.value)
      }
      onFinish()
    }
  }

  private fun initializeDocument() {
    document.body!!.apply {
      position = RELATIVE
      overflowX = HIDDEN
      overflowY = SCROLL
      cursor = DEFAULT

      onmousemove = {
        viewControllers.lastOrNull()?.view?.onMouseMove()
      }
      onresize = {
        viewControllers.forEach { it.view.onResize() }
      }
      window.onunload = {
        viewControllers.forEach { it.onClose() }
      }
      onkeydown = {
        viewControllers
          .lastOrNull()
          ?.view
          ?.onKeyPress(
            (it as? KeyboardEvent)?.code?.let { KeyStroke.fromCode(it) }
          )
      }
    }

    (document.getElementById("viewControllerContainer") as HTMLDivElement).apply {
      position = ABSOLUTE
      zIndex = ZIndex.zVal(0)
      width = Size.vw(100)
      height = Size.vh(100)

      viewControllerFrame = div {
        position = RELATIVE
        width = INHERIT
        height = INHERIT
      }
    }

    spinner = (document.getElementById("spinner") as HTMLDivElement).apply {
      zIndex = ZIndex.zVal(1_000)
      bgColor = Color.black(0.4)
    }
  }

  var spinnerShowing
    get() = spinner.display != NONE
    set(value) {
      if (value) {
        spinner.show()
      } else {
        spinner.hide()
      }
    }

  fun getViewControllers(): List<ViewController> = viewControllers

  fun goTo(url: String) {
    window.open(url, "_self")
  }

  fun pushViewController(viewController: ViewController) {
    if (viewControllers.any { it.name == viewController.name } && DEBUG) {
      App.alert(message = "Attempting to push duplicate ${viewController::class.simpleName}")
    } else {
      val frame = viewControllerFrame
      if (viewController !is AlertViewController) {
        viewControllers.forEach {
          it.view.hide()
        }
      }
      (viewControllers as MutableList).add(viewController)
      frame.appendChild(
        viewController.view.root.apply {
          zIndex = Companion.zVal(
            if (viewController is AlertViewController)
              2_000
            else
              viewControllers.size + 1
          )
        }
      )

      Logger.logScreenStart(viewController::class.simpleName!!)
      viewController.onViewBecameVisible()
      viewController.view.onBecameVisible()
      if (viewControllers.size > 1) {
        (viewControllers[viewControllers.size - 2].view as? ComposeTopLevelView)?.onCoveredByViewController()
      }
    }
  }

  fun popViewController(callback: () -> Unit) {
    unwindToViewController(viewControllers.size - 2, callback)
  }

  fun unwindToViewController(name: String, callback: () -> Unit = {}) {
    unwindToViewController(
      index = viewControllers.indexOfFirst { it::class.simpleName == name },
      callback = callback
    )
  }

  private fun unwindToViewController(index: Int, callback: () -> Unit = {}) {
    if (index < 0 || index > viewControllers.size - 1) {
      App.alert(message = "Attempting to rewind to invalid index $index ${viewControllers.size}")
    } else {
      val unwindTo = viewControllers[index]
      val dismissing = viewControllers.drop(index + 1)
      (viewControllers as MutableList).removeAll(dismissing)
      dismissing.forEach {
        it.view.root.remove()
        it.onPopped()
      }

      (unwindTo.view as? ComposeTopLevelView)?.onBeingRevealedByViewController()
      unwindTo.view.show()
      unwindTo.onUnwind()

      callback()
    }
  }

  val HOME_URL = "/web"
  val PURCHASE_URL = "/purchase"
}

@ExperimentalJsExport
@JsExport
fun initialize(appType: String, cred: String?) {
  AppHelper.appTypeVar = AppType.valueOf(appType)
  App.cred = cred
}

@ExperimentalJsExport
@JsExport
fun initializeHomePage(redirectOnSignIn: String? = null) {
  AppPage.initialize(
    viewController = lazy { StartViewController },
    signInReason = null,
    redirectOnSignIn = redirectOnSignIn,
    onFinish = {
      if (getUrlHash() == "menu") {
        StartViewController.goToPage(MenuPage)
      }
    }
  )
}

@ExperimentalJsExport
@JsExport
fun logOutAndRedirect(url: String) {
  AppHelper.logout {
    AppPage.goTo(url)
  }
}

@ExperimentalJsExport
@JsExport
fun initializeReferralPage(
  signInTitle: String?,
  signInSubtitle: String?,
  redirectOnSignIn: String,
  referredByUserId: String?,
  referredByPromoLinkId: Int?,
  referredByAffiliateId: Int?,
  corporatePlanId: Int?,
  requiredEmailDomain: String?,
) {
  UserPrefs[Key.ReferredByUserId] = referredByUserId
  UserPrefs[Key.ReferredByAffiliateId] = referredByAffiliateId?.toString()
  UserPrefs[Key.ReferredByPromoLinkId] = referredByPromoLinkId?.toString()
  UserPrefs[Key.CorporatePlanId] = corporatePlanId?.toString()

  AppPage.initialize(
    viewController = lazy { StartViewController },
    signInReason = REFERRAL,
    titleLabel = signInTitle,
    subtitleLabel = signInSubtitle,
    redirectOnSignIn = redirectOnSignIn,
    requiredEmailDomain = requiredEmailDomain
  )
}

@ExperimentalJsExport
@JsExport
fun initializePurchasePage(
  alertMessage: String?
) =
  AppPage.initialize(
    viewController = lazy { PurchaseViewController(alertMessage = alertMessage) },
    signInReason = PURCHASE
  )

@ExperimentalJsExport
@JsExport
fun initializeChallengePage(
  title: String,
  subtitle: String,
  buttonText: String,
  defaultEmailNotifications: Boolean,
  defaultPushNotifications: Boolean,
  defaultChallengeUpdateEmails: Boolean,
  emailUnsubscribed: Boolean
) =
  AppPage.initialize(
    viewController = lazy {
      ChallengeEnrollViewController(
        title = title,
        subtitle = subtitle,
        buttonText = buttonText,
        defaultEmailNotifications = defaultEmailNotifications,
        defaultPushNotifications = defaultPushNotifications,
        defaultChallengeUpdateEmails = defaultChallengeUpdateEmails,
        emailUnsubscribed = emailUnsubscribed
      )
    },
    signInReason = CHALLENGE_ENROLLMENT
  )

@ExperimentalJsExport
@JsExport
fun initializeSupportPage() =
  AppPage.initialize(
    viewController = lazy { SupportViewController() },
    signInReason = SUPPORT
  )

@ExperimentalJsExport
@JsExport
fun initializeSequencePlayback(rawSequenceId: String?,
                               debug: Boolean) {
  val sequenceId =
    getUrlHash()
    ?: rawSequenceId
  if (sequenceId == null) {
    App.goHomeOrOpenApp()
    return
  }

  AppPage.initialize(
    viewController = lazy {
      LoadingViewController(sequenceId = sequenceId)
    },
    signInReason = PLAYBACK,
    debug = debug
  )
}

@ExperimentalJsExport
@JsExport
fun initializeRedeemGiftPage(
  giftRedemptionCode: String
) {
  AppPage.initialize(
    viewController = lazy { RedeemGiftViewController(code = giftRedemptionCode) },
    signInReason = GIFT_REDEMPTION
  )
}

@ExperimentalJsExport
@JsExport
fun initializePurchaseGiftPage(
  giftTitle: String,
  giftSubtitle: String,
  products: Array<dynamic>
) {

  AppPage.initialize(
    viewController = lazy {
      PurchaseGiftViewController(
        giftTitle = giftTitle,
        giftSubtitle = giftSubtitle,
        products = products.map { GiftProduct.fromJson(kotlin.js.JSON.stringify(it)) }
      )
    },
    signInReason = GIFT_PURCHASE
  )
}